import { useEffect, useMemo, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Minus,
  Trash2,
  Printer,
  CreditCard,
  Receipt,
  X,
  ChefHat,
  Leaf,
  Drumstick,
  ShoppingBag,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Invoice from "@/components/Invoice";
import { useOutlet } from "@/contexts/OutletContext";

// Resolve the effective price for a menu item, respecting outlet_prices override
const resolvePrice = (mi, outletId) => {
  if (!mi) return 0;
  const op = mi.outlet_prices || {};
  if (outletId && op[outletId] != null && op[outletId] !== "") {
    const v = Number(op[outletId]);
    if (!Number.isNaN(v)) return v;
  }
  return Number(mi.price) || 0;
};

const VegBadge = ({ type }) => {
  const color = type === "veg" ? "#6A7F66" : type === "non-veg" ? "#D95D39" : "#E9C46A";
  return (
    <div
      className="w-4 h-4 border-2 flex items-center justify-center rounded-sm"
      style={{ borderColor: color }}
      title={type}
    >
      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
    </div>
  );
};

export default function POS() {
  const loc = useLocation();
  const nav = useNavigate();
  const { outletId: activeOutletId } = useOutlet();
  const editOrderId = new URLSearchParams(loc.search).get("order");
  const presetTable = new URLSearchParams(loc.search).get("table");
  const autoPay = new URLSearchParams(loc.search).get("pay") === "1";

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");

  const [orderId, setOrderId] = useState(null);
  const [orderType, setOrderType] = useState("dine-in");
  const [tableId, setTableId] = useState(presetTable || "");
  const [tables, setTables] = useState([]);
  const [customer, setCustomer] = useState({ name: "", phone: "" });
  const [cart, setCart] = useState([]); // {menu_item_id,name,price,qty,gst_rate,type}
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("flat");
  const [tip, setTip] = useState(0);
  const [notes, setNotes] = useState("");

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [splitPays, setSplitPays] = useState([{ mode: "cash", amount: 0, reference: "" }]);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [settings, setSettings] = useState({ printer: {} });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/menu-items").then((r) => setItems(r.data));
    api.get("/tables").then((r) => setTables(r.data));
  }, []);

  useEffect(() => {
    if (editOrderId) {
      api.get(`/orders/${editOrderId}`).then((r) => {
        const o = r.data;
        setOrderId(o.id);
        setOrderType(o.type);
        setTableId(o.table_id || "");
        setCustomer({ name: o.customer_name || "", phone: o.customer_phone || "" });
        setCart(o.items || []);
        setDiscount(o.discount || 0);
        setDiscountType(o.discount_type || "flat");
        setTip(o.tip || 0);
        setNotes(o.notes || "");
        if (autoPay && o.status !== "paid" && o.status !== "cancelled") {
          const remaining = Math.max(0, (o.total || 0) - (o.paid_amount || 0));
          setSplitPays([{ mode: "cash", amount: remaining, reference: "" }]);
          setPaymentOpen(true);
        }
      });
    }
  }, [editOrderId, autoPay]);

  const filtered = useMemo(() => {
    let list = items.filter((i) => i.active);
    if (activeCat !== "all") list = list.filter((i) => i.category_id === activeCat);
    const s = search.trim().toLowerCase();
    if (s) {
      list = list.filter((i) =>
        i.name.toLowerCase().includes(s) ||
        (i.code || "").toLowerCase().includes(s)
      );
    }
    return list;
  }, [items, activeCat, search]);

  const addItem = (mi) => {
    const effectivePrice = resolvePrice(mi, activeOutletId);
    setCart((prev) => {
      const idx = prev.findIndex((p) => p.menu_item_id === mi.id);
      if (idx >= 0) {
        const c = [...prev];
        c[idx] = { ...c[idx], qty: c[idx].qty + 1 };
        return c;
      }
      return [
        ...prev,
        {
          menu_item_id: mi.id,
          name: mi.name,
          price: effectivePrice,
          qty: 1,
          gst_rate: mi.gst_rate || 5,
          type: mi.type,
          modifiers: [],
          notes: "",
        },
      ];
    });
  };

  const updateQty = (idx, delta) =>
    setCart((prev) => {
      const c = [...prev];
      c[idx] = { ...c[idx], qty: Math.max(1, c[idx].qty + delta) };
      return c;
    });
  const removeItem = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));

  const totals = useMemo(() => {
    let subtotal = 0, cgst = 0, sgst = 0;
    for (const it of cart) {
      const line = it.price * it.qty;
      subtotal += line;
      const g = (line * (it.gst_rate || 5)) / 100;
      cgst += g / 2;
      sgst += g / 2;
    }
    const disc = discountType === "flat" ? Number(discount || 0) : (subtotal * Number(discount || 0)) / 100;
    const dAmt = Math.min(disc, subtotal);
    const ratio = subtotal > 0 ? (subtotal - dAmt) / subtotal : 0;
    cgst = +(cgst * ratio).toFixed(2);
    sgst = +(sgst * ratio).toFixed(2);
    const tipAmt = Math.max(0, Number(tip || 0));
    const total = +(subtotal - dAmt + cgst + sgst + tipAmt).toFixed(2);
    return { subtotal: +subtotal.toFixed(2), discount: +dAmt.toFixed(2), cgst, sgst, tip: +tipAmt.toFixed(2), total };
  }, [cart, discount, discountType, tip]);

  const buildPayload = () => ({
    type: orderType,
    table_id: orderType === "dine-in" ? tableId || null : null,
    customer_name: customer.name || null,
    customer_phone: customer.phone || null,
    items: cart.map((c) => ({
      menu_item_id: c.menu_item_id,
      name: c.name,
      price: c.price,
      qty: c.qty,
      gst_rate: c.gst_rate || 5,
      modifiers: c.modifiers || [],
      notes: c.notes || "",
    })),
    discount: Number(discount || 0),
    discount_type: discountType,
    tip: Number(tip || 0),
    notes,
  });

  const resetCart = () => {
    setOrderId(null);
    setCart([]);
    setDiscount(0);
    setTip(0);
    setNotes("");
    setCustomer({ name: "", phone: "" });
    setTableId("");
    setOrderType("dine-in");
  };

  const saveOrder = async (then) => {
    if (cart.length === 0) {
      toast.error("Add items first");
      return null;
    }
    try {
      let res;
      if (orderId) {
        res = await api.put(`/orders/${orderId}`, buildPayload());
      } else {
        res = await api.post("/orders", buildPayload());
        setOrderId(res.data.id);
      }
      toast.success(orderId ? "Order updated" : "Order saved");
      if (then) await then(res.data);
      return res.data;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  };

  const sendKOT = async () => {
    const o = await saveOrder();
    if (!o) return;
    const p = settings.printer || {};
    try {
      if (p.enabled && p.type === "network") {
        await api.post(`/print/kot/${o.id}`);
        toast.success("KOT printed to kitchen");
      } else {
        await api.post(`/orders/${o.id}/kot`);
        if (o.token_no) {
          toast.success(`KOT sent · Token ${o.token_no}`, { duration: 4000 });
        } else {
          toast.success("KOT sent to kitchen");
        }
        if (p.enabled && p.auto_print_kot) {
          setTimeout(() => window.print(), 200);
        }
      }
      // After KOT is sent, free the cashier to take the next table's order.
      setMobileCartOpen(false);
      resetCart();
      if (o.type === "dine-in") {
        // Send cashier back to table picker so they can serve the next table
        setTimeout(() => nav("/tables", { replace: true }), 350);
      } else {
        nav("/pos", { replace: true });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPay = async () => {
    const o = await saveOrder();
    if (!o) return;
    setSplitPays([{ mode: "cash", amount: o.total, reference: "" }]);
    setPaymentOpen(true);
  };

  const submitPay = async () => {
    const sum = splitPays.reduce((a, b) => a + Number(b.amount || 0), 0);
    if (sum + 0.01 < totals.total) {
      toast.error(`Amount short by ${inr(totals.total - sum)}`);
      return;
    }
    try {
      const { data } = await api.post(
        `/orders/${orderId}/pay`,
        splitPays.filter((p) => Number(p.amount) > 0)
      );
      toast.success("Payment recorded");
      setPaymentOpen(false);
      setInvoiceOrder(data);
      if (settings.printer?.enabled && settings.printer?.auto_print_bill) {
        setTimeout(() => window.print(), 300);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const printInvoice = () => {
    window.print();
  };

  const newOrder = () => {
    setInvoiceOrder(null);
    resetCart();
    nav("/pos", { replace: true });
  };

  return (
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-3.5rem)] md:lg:h-screen lg:overflow-hidden pb-24 lg:pb-0" data-testid="pos-page">
      {/* MENU PANEL */}
      <div className="flex-1 flex flex-col p-4 sm:p-5 lg:overflow-hidden min-w-0">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-heading font-extrabold text-xl sm:text-2xl text-[#2D2C2A]">POS Billing</h1>
            <p className="text-xs text-[#7A7873] hidden sm:block">Pick items, build the order, capture payment.</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7873]" size={16} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search dish or code…"
              className="pl-9 h-10 border-[#EBE8E2]"
              data-testid="pos-search"
            />
          </div>
        </div>

        <Tabs value={activeCat} onValueChange={setActiveCat} className="mb-4">
          <TabsList className="bg-white border border-[#EBE8E2] p-1 h-auto flex-wrap justify-start">
            <TabsTrigger value="all" data-testid="cat-all" className="data-[state=active]:bg-[#FBE9E2] data-[state=active]:text-[#D95D39]">All</TabsTrigger>
            {categories.map((c) => (
              <TabsTrigger
                key={c.id}
                value={c.id}
                data-testid={`cat-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="data-[state=active]:bg-[#FBE9E2] data-[state=active]:text-[#D95D39]"
              >
                {c.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex-1 lg:overflow-auto pr-0 lg:pr-1">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filtered.map((mi) => (
              <button
                key={mi.id}
                onClick={() => addItem(mi)}
                data-testid={`menu-item-${mi.id}`}
                className="text-left bg-white border border-[#EBE8E2] rounded-lg p-3 hover:border-[#D95D39] hover:shadow-sm transition active:scale-[0.98]"
              >
                {mi.image_url ? (
                  <div className="w-full h-24 rounded-md mb-2 overflow-hidden bg-[#F4F2EE]">
                    <img src={mi.image_url} alt={mi.name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-full h-24 rounded-md mb-2 bg-[#F4F2EE] flex items-center justify-center text-[#7A7873]">
                    {mi.type === "veg" ? <Leaf size={28} /> : <Drumstick size={28} />}
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-sm text-[#2D2C2A] line-clamp-2">{mi.name}</div>
                  <VegBadge type={mi.type} />
                </div>
                {mi.code && (
                  <div className="text-[10px] text-[#7A7873] font-mono-num mt-0.5">{mi.code}</div>
                )}
                <div className="mt-2 font-mono-num text-[#D95D39] font-semibold text-sm">{inr(resolvePrice(mi, activeOutletId))}</div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full py-12 text-center text-[#7A7873] text-sm">No items found.</div>
            )}
          </div>
        </div>
      </div>

      {/* ORDER DRAWER */}
      {mobileCartOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileCartOpen(false)}
        />
      )}
      <aside
        className={`bg-white border-l border-[#EBE8E2] flex flex-col fixed lg:static inset-y-0 right-0 w-full max-w-md lg:max-w-none lg:w-[400px] z-50 transition-transform duration-300 lg:translate-x-0 ${
          mobileCartOpen ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="order-drawer"
      >
        <div className="p-4 border-b border-[#EBE8E2]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-lg text-[#2D2C2A]">
              {orderId ? "Editing Order" : "New Order"}
            </h2>
            <div className="flex items-center gap-2">
              {orderId && (
                <button onClick={resetCart} className="text-xs text-[#7A7873] hover:text-[#D95D39]" data-testid="new-order-btn">
                  + New
                </button>
              )}
              <button
                className="lg:hidden p-1.5 rounded-md hover:bg-[#F4F2EE]"
                onClick={() => setMobileCartOpen(false)}
                aria-label="Close cart"
                data-testid="close-mobile-cart"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
            {["dine-in", "takeaway", "delivery", "zomato", "swiggy"].map((t) => (
              <button
                key={t}
                onClick={() => setOrderType(t)}
                data-testid={`order-type-${t}`}
                className={`text-xs py-2 rounded-md border capitalize font-medium transition ${
                  orderType === t
                    ? "bg-[#D95D39] text-white border-[#D95D39]"
                    : "bg-white text-[#2D2C2A] border-[#EBE8E2] hover:border-[#D95D39]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {orderType === "dine-in" && (
            <Select value={tableId} onValueChange={setTableId}>
              <SelectTrigger className="h-9 border-[#EBE8E2]" data-testid="table-select">
                <SelectValue placeholder="Select table" />
              </SelectTrigger>
              <SelectContent>
                {tables.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} · {t.area} ({t.capacity}p)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {orderType !== "dine-in" && (
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Customer name"
                value={customer.name}
                onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
                className="h-9 border-[#EBE8E2]"
                data-testid="customer-name-input"
              />
              <Input
                placeholder="Phone"
                value={customer.phone}
                onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))}
                className="h-9 border-[#EBE8E2]"
                data-testid="customer-phone-input"
              />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-2" data-testid="cart-items">
          {cart.length === 0 && (
            <div className="text-center text-[#7A7873] text-sm py-12">
              <Receipt className="mx-auto mb-2 opacity-40" size={32} />
              No items in cart.<br />Tap menu items to add.
            </div>
          )}
          {cart.map((it, idx) => (
            <div key={idx} className="border border-[#EBE8E2] rounded-md p-3" data-testid={`cart-item-${idx}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <VegBadge type={it.type} />
                    <div className="font-medium text-sm text-[#2D2C2A]">{it.name}</div>
                  </div>
                  <div className="text-xs text-[#7A7873] mt-0.5 font-mono-num">{inr(it.price)} × {it.qty}</div>
                </div>
                <button
                  onClick={() => removeItem(idx)}
                  className="text-[#7A7873] hover:text-[#E76F51]"
                  data-testid={`remove-item-${idx}`}
                >
                  <X size={14} />
                </button>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-1">
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-7 w-7 border-[#EBE8E2]"
                    onClick={() => updateQty(idx, -1)}
                    data-testid={`qty-dec-${idx}`}
                  >
                    <Minus size={12} />
                  </Button>
                  <span className="w-7 text-center font-mono-num text-sm">{it.qty}</span>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-7 w-7 border-[#EBE8E2]"
                    onClick={() => updateQty(idx, 1)}
                    data-testid={`qty-inc-${idx}`}
                  >
                    <Plus size={12} />
                  </Button>
                </div>
                <div className="font-mono-num font-semibold text-sm text-[#2D2C2A]">{inr(it.price * it.qty)}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-[#EBE8E2] p-4 space-y-3 bg-[#FBFAF8]">
          <div className="flex items-center gap-2">
            <Select value={discountType} onValueChange={setDiscountType}>
              <SelectTrigger className="h-9 w-24 border-[#EBE8E2]" data-testid="discount-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="flat">₹ flat</SelectItem>
                <SelectItem value="percent">% off</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="Discount"
              className="h-9 border-[#EBE8E2]"
              data-testid="discount-input"
            />
          </div>

          <div className="flex items-center gap-2">
            <div className="h-9 w-24 px-2 flex items-center text-xs text-[#7A7873] border border-[#EBE8E2] bg-white rounded-md">Tip (₹)</div>
            <Input
              type="number"
              value={tip}
              onChange={(e) => setTip(e.target.value)}
              placeholder="Waiter tip"
              className="h-9 border-[#EBE8E2]"
              data-testid="tip-input"
            />
          </div>

          <div className="space-y-1 text-sm font-mono-num">
            <div className="flex justify-between text-[#7A7873]"><span>Subtotal</span><span>{inr(totals.subtotal)}</span></div>
            {totals.discount > 0 && (
              <div className="flex justify-between text-[#6A7F66]"><span>Discount</span><span>− {inr(totals.discount)}</span></div>
            )}
            <div className="flex justify-between text-[#7A7873]"><span>CGST</span><span>{inr(totals.cgst)}</span></div>
            <div className="flex justify-between text-[#7A7873]"><span>SGST</span><span>{inr(totals.sgst)}</span></div>
            {totals.tip > 0 && (
              <div className="flex justify-between text-[#7A7873]"><span>Tip</span><span>{inr(totals.tip)}</span></div>
            )}
            <div className="flex justify-between font-bold text-[#2D2C2A] text-lg pt-2 border-t border-[#EBE8E2]">
              <span>Total</span>
              <span data-testid="cart-total">{inr(totals.total)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button
              variant="outline"
              className="h-10 border-[#6A7F66] text-[#6A7F66] hover:bg-[#6A7F66] hover:text-white"
              onClick={sendKOT}
              data-testid="kot-button"
            >
              <ChefHat size={16} className="mr-1.5" /> Save & KOT
            </Button>
            <Button
              className="h-10 bg-[#D95D39] hover:bg-[#C24F2C] text-white font-semibold"
              onClick={openPay}
              data-testid="pay-button"
            >
              <CreditCard size={16} className="mr-1.5" /> Pay {inr(totals.total)}
            </Button>
          </div>
        </div>
      </aside>

      {/* MOBILE CART BAR */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#EBE8E2] px-4 py-3 flex items-center justify-between z-30 shadow-[0_-2px_8px_rgba(0,0,0,0.05)]">
        <div>
          <div className="text-xs text-[#7A7873]">{cart.length} item{cart.length !== 1 ? "s" : ""}</div>
          <div className="font-mono-num font-extrabold text-lg text-[#2D2C2A]" data-testid="mobile-cart-total">
            {inr(totals.total)}
          </div>
        </div>
        <Button
          onClick={() => setMobileCartOpen(true)}
          className="bg-[#D95D39] hover:bg-[#C24F2C] h-11 px-5"
          data-testid="open-mobile-cart"
          disabled={cart.length === 0}
        >
          <ShoppingBag size={16} className="mr-1.5" /> View Cart
        </Button>
      </div>

      {/* PAYMENT DIALOG */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Capture Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-2xl font-heading font-extrabold text-[#D95D39] font-mono-num">
              {inr(totals.total)} <span className="text-xs text-[#7A7873] font-normal">due</span>
            </div>
            {splitPays.map((p, i) => (
              <div key={i} className="grid grid-cols-[120px_1fr_auto] gap-2">
                <Select value={p.mode} onValueChange={(v) => setSplitPays((arr) => arr.map((x, j) => (j === i ? { ...x, mode: v } : x)))}>
                  <SelectTrigger className="h-10 border-[#EBE8E2]" data-testid={`pay-mode-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                    <SelectItem value="zomato">Zomato</SelectItem>
                    <SelectItem value="swiggy">Swiggy</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  value={p.amount}
                  onChange={(e) => setSplitPays((arr) => arr.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                  className="h-10 border-[#EBE8E2]"
                  data-testid={`pay-amount-${i}`}
                />
                {splitPays.length > 1 && (
                  <Button variant="ghost" size="icon" onClick={() => setSplitPays((arr) => arr.filter((_, j) => j !== i))}>
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSplitPays((a) => [...a, { mode: "cash", amount: 0, reference: "" }])}
              data-testid="add-split-payment"
            >
              + Split payment
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={submitPay} data-testid="confirm-payment">
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* INVOICE */}
      <Dialog open={!!invoiceOrder} onOpenChange={(v) => !v && newOrder()}>
        <DialogContent className="invoice-print-dialog bg-white max-w-md p-0 max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          {invoiceOrder && <Invoice order={invoiceOrder} />}
          <div className="flex gap-2 p-4 border-t border-[#EBE8E2] no-print">
            <Button variant="outline" className="flex-1" onClick={newOrder} data-testid="invoice-new-order">New Order</Button>
            <Button className="flex-1 bg-[#D95D39] hover:bg-[#C24F2C]" onClick={printInvoice} data-testid="invoice-print">
              <Printer size={16} className="mr-1.5" /> Print
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
